import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "TrustSearchReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: '.sheet',
      required: false
    },
    trust_search_data: {
      type: Array,
      default: () => [],
      required: false,
    },
    client_user_reference: {
      type: String,
      default: () => `TRUST-${Date.now()}`,
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        trust_information: false,
        trustees_details: false,
        transaction_details: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    has_selected_tables() {
      return Object.values(this.export_options).some(option => option);
    },
    safe_trust_search_data() {
      return this.trust_search_data || [];
    },
    
    // Get the first trust (assuming single trust search result)
    primary_trust() {
      return this.safe_trust_search_data[0] || {};
    },
    
    // Basic Trust Information
    trust_name() {
      return this.primary_trust.TrustName || 'N/A';
    },
    
    file_number() {
      return this.primary_trust.FileNumber || 'N/A';
    },
    
    masters_office() {
      return this.primary_trust.MastersOffice || 'N/A';
    },
    
    authorization_date() {
      return this.primary_trust.AuthorisationDate || null;
    },
    
    formatted_authorization_date() {
      if (this.authorization_date) {
        return this.formatDate(this.authorization_date);
      }
      return 'N/A';
    },
    
    trustees_list() {
      return this.primary_trust.Trustees || [];
    },
    
    total_trustees() {
      return this.trustees_list.length;
    },
    
    // Verification status computations
    active_trustees_count() {
      return this.trustees_list.filter(trustee => 
        trustee.FullNames && trustee.FullNames.trim() !== ''
      ).length;
    },
    
    verified_trustees_count() {
      return this.trustees_list.filter(trustee => 
        trustee.IDNumber && trustee.IDNumber.trim() !== '' && 
        trustee.Age !== '0' && trustee.Gender !== 'NotSet'
      ).length;
    },
    
    trust_found_status() {
      return this.trust_name !== 'N/A' ? 'Yes' : 'No';
    },
    
    // Overall verification status
    overall_verification_status() {
      if (this.trust_found_status === 'Yes' && this.total_trustees > 0) {
        return 'Verified';
      }
      return 'Incomplete';
    },
    
    // Badge classes and icons
    verification_status_badge_class() {
      return this.overall_verification_status === 'Verified' ? 'status-good' : 'status-bad';
    },
    
    verification_status_icon() {
      return this.overall_verification_status === 'Verified' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle';
    },
    
    current_date() {
      return new Date().toISOString();
    },
    
    formatted_current_date() {
      return this.formatDate(this.current_date);
    },
    
    // Report metadata
    report_reference() {
      return this.client_user_reference;
    },
    
    modal_instance() {
      var modal = new window.bootstrap.Modal(document.getElementById(`export-modal-${this.report_reference}`))
      return modal;
    }
  },
  
  methods: {
    close_modal() {
      this.modal_instance.hide();
    },
    
    // Utility methods
    formatDate(date) {
      return format_date(date);
    },
    
    formatTrusteeDate(date) {
      if (!date || date === '0001-01-01T00:00:00') {
        return 'Not Available';
      }
      return this.formatDate(date);
    },
    
    formatGender(gender) {
      if (gender === 'NotSet' || !gender) {
        return 'Not Available';
      }
      return gender;
    },
    
    getTrusteeStatusBadgeClass(trustee) {
      if (trustee.IDNumber && trustee.Age !== '0' && trustee.Gender !== 'NotSet') {
        return 'bg-success';
      } else if (trustee.FullNames) {
        return 'bg-warning';
      }
      return 'bg-danger';
    },
    
    getTrusteeStatusIcon(trustee) {
      if (trustee.IDNumber && trustee.Age !== '0' && trustee.Gender !== 'NotSet') {
        return 'fas fa-check';
      } else if (trustee.FullNames) {
        return 'fas fa-exclamation';
      }
      return 'fas fa-times';
    },
    
    getTrusteeStatusText(trustee) {
      if (trustee.IDNumber && trustee.Age !== '0' && trustee.Gender !== 'NotSet') {
        return 'Verified';
      } else if (trustee.FullNames) {
        return 'Partial';
      }
      return 'Incomplete';
    },
    
    // Export functionality
    show_export_modal() {
      this.export_options.trust_information = true;
      this.export_options.trustees_details = true;
      this.export_options.transaction_details = true;
      this.export_options.raw_json = false;
      this.modal_instance.show();
    },
    
    async load_export_libraries() {
      if (!this.libraries_loaded.papa_parse) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.0/papaparse.min.js');
        this.libraries_loaded.papa_parse = true;
      }
      
      if (!this.libraries_loaded.sheetjs) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.17.0/xlsx.full.min.js');
        this.libraries_loaded.sheetjs = true;
      }
    },
    
    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    
    async export_data() {
      await this.load_export_libraries();
      
      if (this.export_format === 'csv') {
        await this.export_csv();
      } else if (this.export_format === 'excel') {
        await this.export_excel();
      } else if (this.export_format === 'json') {
        await this.export_json();
      }
      
      // Close modal
      this.close_modal();
    },
    
    // Prepare trust information data
    prepare_trust_information_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "Trust Name": this.trust_name,
          "File Number": this.file_number,
          "Masters Office": this.masters_office,
          "Authorization Date": this.formatted_authorization_date,
          "Verification Status": this.overall_verification_status,
          "Total Trustees": this.total_trustees,
          "Active Trustees": this.active_trustees_count,
          "Verified Trustees": this.verified_trustees_count,
          "Trust Found": this.trust_found_status,
          "Generated Date": this.formatted_current_date
        }
      ];
    },
    
    // Prepare trustees details data
    prepare_trustees_details_data() {
      return this.trustees_list.map((trustee, index) => ({
        "Trustee Number": index + 1,
        "Full Names": trustee.FullNames,
        "ID Number": trustee.IDNumber || 'Not Available',
        "Date of Birth": this.formatTrusteeDate(trustee.DateOfBirth),
        "Age": trustee.Age === '0' ? 'Not Available' : trustee.Age,
        "Gender": this.formatGender(trustee.Gender),
        "Status": this.getTrusteeStatusText(trustee)
      }));
    },
    
    // Prepare transaction details data
    prepare_transaction_details_data() {
      return [
        {
          "Transaction Reference": this.client_user_reference,
          "Generated Date": this.formatted_current_date,
          "Trust Name Searched": this.trust_name,
          "File Number": this.file_number,
          "Masters Office": this.masters_office,
          "Authorization Date": this.formatted_authorization_date,
          "Search Status": this.trust_found_status,
          "Total Records Found": this.safe_trust_search_data.length,
          "Total Trustees": this.total_trustees,
          "Verified Trustees": this.verified_trustees_count
        }
      ];
    },
    
    // Prepare raw JSON data
    prepare_raw_json_data() {
      return {
        report_metadata: {
          report_reference: this.report_reference,
          client_user_reference: this.client_user_reference,
          generated_date: this.formatted_current_date,
          export_date: new Date().toISOString(),
          report_type: 'Trust Search Verification Report'
        },
        raw_trust_search_data: this.safe_trust_search_data
      };
    },
    
    // Export methods
    async export_csv() {
      const Papa = window.Papa;
      const current_date = new Date().toISOString().split('T')[0];
      
      // Map section keys to their data preparation methods and file suffixes
      const sectionMap = [
        {
          key: 'trust_information',
          label: 'Trust_Information',
          prepare: this.prepare_trust_information_data
        },
        {
          key: 'trustees_details',
          label: 'Trustees_Details',
          prepare: this.prepare_trustees_details_data
        },
        {
          key: 'transaction_details',
          label: 'Transaction_Details',
          prepare: this.prepare_transaction_details_data
        }
      ];

      // For each selected section, generate and download a CSV
      for (const section of sectionMap) {
        if (this.export_options[section.key]) {
          const data = section.prepare.call(this);
          if (data && data.length > 0) {
            const csv = Papa.unparse(data);
            const filename = `Trust_Search_Verification_Report_${section.label}_${current_date}.csv`;
            this.download_file(csv, filename, 'text/csv');
          }
        }
      }
    },
    
    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = new Date().toISOString().split('T')[0];
      const workbook = XLSX.utils.book_new();
      
      if (this.export_options.trust_information) {
        const trust_data = this.prepare_trust_information_data();
        const worksheet = XLSX.utils.json_to_sheet(trust_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Trust Information');
      }
      
      if (this.export_options.trustees_details) {
        const trustees_data = this.prepare_trustees_details_data();
        const worksheet = XLSX.utils.json_to_sheet(trustees_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Trustees Details');
      }
      
      if (this.export_options.transaction_details) {
        const transaction_data = this.prepare_transaction_details_data();
        const worksheet = XLSX.utils.json_to_sheet(transaction_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Transaction Details');
      }
      
      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        // For Excel, create a simple table with the JSON as text
        const json_table = [
          {
            'JSON Data': JSON.stringify(json_data, null, 2)
          }
        ];
        const worksheet = XLSX.utils.json_to_sheet(json_table);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Raw JSON Data');
      }
      
      const filename = `Trust_Search_Verification_Report_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },
    
    async export_json() {
      const current_date = new Date().toISOString().split('T')[0];
      const export_data = {};
      
      if (this.export_options.trust_information) {
        export_data.trust_information = this.prepare_trust_information_data()[0];
      }
      
      if (this.export_options.trustees_details) {
        export_data.trustees_details = this.prepare_trustees_details_data();
      }
      
      if (this.export_options.transaction_details) {
        export_data.transaction_details = this.prepare_transaction_details_data()[0];
      }
      
      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }
      
      const json = JSON.stringify(export_data, null, 2);
      const filename = `Trust_Search_Verification_Report_${current_date}.json`;
      this.download_file(json, filename, 'application/json');
    },
    
    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
};